import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useWorkflowExecutionsInfinite,
  type WorkflowExecution,
  type WorkflowExecutionListFilters,
} from '@/hooks/useWorkflowExecutions';
import { DATE_GROUP_ORDER, getDateGroup, type DateGroup } from '@/utils/date';
import { FEATURE_AREAS, useTrackListSearch } from '@/lib/analytics';

/** Standard debounce for a search-a-list input in this codebase. */
const SEARCH_DEBOUNCE_MS = 300;

const PINNED_FILTERS: WorkflowExecutionListFilters = { pinned: true };
const UNPINNED_FILTERS: WorkflowExecutionListFilters = { pinned: false };

/**
 * The timestamp used for grouping: the first user message's `updated_at` when it
 * exists, otherwise the execution's own. That answers "when was this
 * conversation actually used" rather than when the row was touched.
 */
const timestampOf = (execution: WorkflowExecution): string => {
  const firstUserMessage = execution.workflow_execution_chat_history?.find(
    message => message.role === 'user',
  );
  return firstUserMessage?.updated_at ?? execution.updated_at;
};

export interface ChatDateGroup {
  group: DateGroup;
  executions: WorkflowExecution[];
}

/**
 * Search, paging and grouping for the chat list: a pinned shelf plus the
 * unpinned rows bucketed by relative date.
 *
 * Runs two independent `useWorkflowExecutionsInfinite` queries — one pinned,
 * one not — rather than filtering one list client-side, so the pinned shelf
 * still shows a pinned chat that has fallen off the unpinned list's loaded
 * pages. Date buckets reuse the shared `DATE_GROUP_ORDER` / `getDateGroup`
 * helpers rather than defining their own, so the sidebar cannot drift from
 * anywhere else that groups by relative date.
 */
export function useChatList(workflowInstanceId: string) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  const enabled = Boolean(workflowInstanceId);
  const pinnedQuery = useWorkflowExecutionsInfinite(workflowInstanceId, enabled, debouncedSearch, PINNED_FILTERS);
  const unpinnedQuery = useWorkflowExecutionsInfinite(workflowInstanceId, enabled, debouncedSearch, UNPINNED_FILTERS);

  const isLoading = pinnedQuery.isLoading || unpinnedQuery.isLoading;
  const pinnedError = pinnedQuery.error;
  const error = unpinnedQuery.error;

  /** Server-side search; the count is only meaningful once the debounced term settles on both requests. */
  const searchApplied = debouncedSearch === search.trim();
  useTrackListSearch({
    surface: 'chat_history',
    featureArea: FEATURE_AREAS.chat,
    query: search,
    resultCount: searchApplied && !isLoading
      ? pinnedQuery.executions.length + unpinnedQuery.executions.length
      : undefined,
  });

  const dateGroups = useMemo<ChatDateGroup[]>(() => {
    const executions = unpinnedQuery.executions;
    if (executions.length === 0) return [];

    const buckets = new Map<DateGroup, WorkflowExecution[]>();
    for (const execution of executions) {
      const group = getDateGroup(timestampOf(execution));
      const bucket = buckets.get(group) ?? [];
      bucket.push(execution);
      buckets.set(group, bucket);
    }

    return DATE_GROUP_ORDER.flatMap(group => {
      const bucket = buckets.get(group);
      return bucket ? [{ group, executions: bucket }] : [];
    });
  }, [unpinnedQuery.executions]);

  const hasNextPage = Boolean(pinnedQuery.hasNextPage) || Boolean(unpinnedQuery.hasNextPage);
  const isFetchingNextPage = pinnedQuery.isFetchingNextPage || unpinnedQuery.isFetchingNextPage;

  /** Advances whichever of the two lists still has another page. */
  const fetchNextPage = useCallback(() => {
    if (pinnedQuery.hasNextPage && !pinnedQuery.isFetchingNextPage) pinnedQuery.fetchNextPage();
    if (unpinnedQuery.hasNextPage && !unpinnedQuery.isFetchingNextPage) unpinnedQuery.fetchNextPage();
  }, [pinnedQuery, unpinnedQuery]);

  return {
    search,
    setSearch,
    /** The term the rows on screen were actually fetched for, so a highlight never marks a word the list has not caught up to. */
    appliedSearch: debouncedSearch,
    isSearching: debouncedSearch.length > 0,
    pinned: pinnedQuery.executions,
    dateGroups,
    /** Either request failing says nothing about whether the user has chats, so both disqualify "empty" rather than being left to callers to remember. */
    isEmpty: !isLoading && !pinnedError && !error && pinnedQuery.executions.length === 0 && dateGroups.length === 0,
    isLoading,
    pinnedError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
